/* globals document navigator */

import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { autobind } from 'core-decorators';
import classNames from 'classnames';

import {
  fetchChargers,
  chargersNearUser,
  chargersNearUserNext,
  fetchChargersNearIp,
  filterChargers,
  changeMap,
  chargerMapClick,
  geolocation,
  setCenterOnCurrentLocation,
} from 'actions';
import { byIdShape, latLngShape } from 'utils/propTypes';

import MapContainer from 'AppComponents/MapContainer';
import { Checkbox } from 'AppComponents/Forms';

import ChargerMap from './components/ChargerMap';
import ChargerList from './components/ChargerList';
import ChargerPreview from './components/ChargerPreview';

import './styles.scss';


const filtersEquals = (prev, next) => {
  if (prev.superchargers !== next.superchargers) return false;
  if (prev.destinationChargers !== next.destinationChargers) return false;
  return true;
};

const mapCenterSigChange = (prev, next, margin = 0.01) => {
  if (Math.abs(prev.lat - next.lat) > margin) return true;
  if (Math.abs(prev.lng - next.lng) > margin) return true;
  return false;
};

const htmlSizeClass = 'full-height';


class Home extends Component {

  static propTypes = {
    changeMap: PropTypes.func.isRequired,
    chargerMap: PropTypes.shape({
      center: latLngShape,
      zoom: PropTypes.number,
      selectedCharger: PropTypes.number,
    }).isRequired,
    chargerMapClick: PropTypes.func.isRequired,
    chargersById: byIdShape.isRequired,
    chargersNearUser: PropTypes.shape({
      filtered: PropTypes.array,
      loading: PropTypes.bool,
      pagination: PropTypes.object,
    }).isRequired,
    chargersNearUserNextRequest: PropTypes.func.isRequired,
    currentLocation: PropTypes.shape({ coords: latLngShape, allowed: PropTypes.bool }),
    fetchChargers: PropTypes.func.isRequired,
    fetchChargersNearIp: PropTypes.func.isRequired,
    filterChargers: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      superchargers: PropTypes.bool,
      destinationChargers: PropTypes.bool,
    }).isRequired,
    geolocationFailure: PropTypes.func.isRequired,
    geolocationSuccess: PropTypes.func.isRequired,
    locationFromIp: latLngShape,
    mappedChargers: PropTypes.arrayOf(PropTypes.number).isRequired,
    setCenterOnCurrentLocation: PropTypes.func.isRequired,
  };

  static defaultProps = {
    currentLocation: null,
    locationFromIp: null,
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
    appClass: PropTypes.string.isRequired,
    isCarBrowser: PropTypes.bool.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.mapLoaded = false;
    this._mapHasCurLocButton = false;
    this._currentLocInit = false;
    this.state = {
      chargerHover: null,
      markerHover: null,
    };
    if (typeof document !== 'undefined') {
      const htmlClassName = document.documentElement.className;
      if (htmlClassName.indexOf(htmlSizeClass) === -1) {
        document.documentElement.className = `${document.documentElement.className} ${htmlSizeClass}`;
      }
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      !filtersEquals(this.props.filters, nextProps.filters) ||
      mapCenterSigChange(this.props.chargerMap.center, nextProps.chargerMap.center)
    ) {
      this.fetchNewChargersList(nextProps);
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      const htmlClassName = document.documentElement.className;
      const index = htmlClassName.indexOf(htmlSizeClass);
      if (index !== -1) {
        const re = new RegExp(`\\s?${htmlSizeClass}`);
        document.documentElement.className = htmlClassName.replace(re, '');
      }
    }
  }

  @autobind
  onMapLoaded({ map, maps }) {
    this.mapLoaded = true;
    this.map = map;
    this.maps = maps;

    if (!this.props.currentLocation) this.centerOnCurrentLocation();
    else if (!this.props.locationFromIp) this.props.fetchChargersNearIp();

    if (this.props.currentLocation) {
      if (this.props.currentLocation.allowed) this.addCurrentLocationButton(map);
    }

    // if (!this.context.isCarBrowser) disableTouchScroll('map-container');

    this.maps.event.addListener(this.map, 'dragend', this.handleMapDragEnd.bind(this));
  }

  getCurrentLocation(success) {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.watchPosition((position) => {
        this.props.geolocationSuccess({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        success(position);
      }, (error) => {
        const obj = {
          allowed: true,
          reason: {
            code: error.code,
            message: error.message,
          },
        };
        if (error.code === error.PERMISSION_DENIED) obj.allowed = false;
        this.props.geolocationFailure(obj);
      });
    }
  }

  centerOnCurrentLocation() {
    this.getCurrentLocation((coords) => {
      if (!this._currentLocInit) {
        this.addCurrentLocationButton(this.map);
        this.props.changeMap({
          lat: coords.coords.latitude,
          lng: coords.coords.longitude,
        }, 13);
        this._currentLocInit = true;
      }
    });
  }

  @autobind
  handleMapClick({ event }) {
    let markerClicked = false;
    for (let element = event.target; element; element = element.parentNode) {
      if (element.nodeName === 'DIV' && element.className.includes('map-icon')) {
        markerClicked = true;
      }
    }
    if (!markerClicked) this.props.chargerMapClick(null);
  }

  handleMapDragEnd() {
    this.props.setCenterOnCurrentLocation(false);
  }

  @autobind
  handleMarkerClick(key, cluster) {
    this.props.chargerMapClick(cluster.charger.pk);
    this.props.fetchChargers({
      include: [cluster.charger.pk],
    });
  }

  @autobind
  handleClusterClick(key, cluster) {
    const center = {
      lat: cluster.geometry.coordinates[1],
      lng: cluster.geometry.coordinates[0],
    };
    const zoom = this.props.chargerMap.zoom + 1;
    this.props.changeMap(center, zoom);
  }

  @autobind
  handleMapBoundsChange({ center, zoom }) {
    if (this.mapLoaded) this.props.changeMap(center, zoom);
  }

  fetchNewChargersList(props) {
    const { chargerMap, filters, currentLocation, chargersNearUserRequest } = props || this.props;
    const args = {
      lat: chargerMap.center.lat,
      lng: chargerMap.center.lng,
      limit: 20,
    };
    if (
      (filters.superchargers || filters.destinationChargers) &&
      filters.superchargers !== filters.destinationChargers
    ) {
      args.is_supercharger = filters.superchargers;
    }
    if (currentLocation && currentLocation.coords) {
      args.user_lat = currentLocation.coords.lat;
      args.user_lng = currentLocation.coords.lng;
    }
    chargersNearUserRequest(args);
  }

  @autobind
  handleDestinationChargersCheckbox(destinationChargers) {
    const filters = Object.assign({}, this.props.filters, { destinationChargers });
    this.props.filterChargers(filters);
  }

  @autobind
  handleSuperchargersCheckbox(superchargers) {
    const filters = Object.assign({}, this.props.filters, { superchargers });
    this.props.filterChargers(filters);
  }

  goToCurrentLocation() {
    this.props.setCenterOnCurrentLocation(true);
    if (this.props.currentLocation.coords) {
      this.props.changeMap(this.props.currentLocation.coords, this.props.chargerMap.zoom);
    } else {
      this.centerOnCurrentLocation();
    }
  }

  addCurrentLocationButton(map) {
    const { isCarBrowser } = this.context;
    if (!this._mapHasCurLocButton) {
      const controlDiv = document.createElement('div');

      const firstChild = document.createElement('button');
      firstChild.title = 'Your Location';
      if (isCarBrowser) {
        firstChild.className = 'btn current-location';
      } else {
        firstChild.style.backgroundColor = '#fff';
        firstChild.style.border = 'none';
        firstChild.style.outline = 'none';
        firstChild.style.width = '28px';
        firstChild.style.height = '28px';
        firstChild.style.borderRadius = '2px';
        firstChild.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
        firstChild.style.cursor = 'pointer';
        firstChild.style.marginRight = '10px';
        firstChild.style.padding = '0px';
      }
      controlDiv.appendChild(firstChild);

      const secondChild = document.createElement('div');
      if (isCarBrowser) {
        secondChild.style.display = 'none';
      } else {
        secondChild.style.margin = '5px';
        secondChild.style.width = '18px';
        secondChild.style.height = '18px';
        secondChild.style.backgroundImage = 'url(https://maps.gstatic.com/tactile/mylocation/mylocation-sprite-2x.png)';
        secondChild.style.backgroundSize = '180px 18px';
        secondChild.style.backgroundPosition = '0px 0px';
        secondChild.style.backgroundRepeat = 'no-repeat';
        secondChild.id = 'you_location_img';
      }
      firstChild.appendChild(secondChild);

      firstChild.addEventListener('click', this.goToCurrentLocation.bind(this));

      controlDiv.index = 1;
      map.controls[
        isCarBrowser ? this.maps.ControlPosition.TOP_RIGHT : this.maps.ControlPosition.RIGHT_BOTTOM
      ].push(controlDiv);
      this._mapHasCurLocButton = true;
    }
  }

  @autobind
  handleLoadMoreChargers() {
    const { pagination } = this.props.chargersNearUser;
    if (pagination.next) {
      this.props.chargersNearUserNextRequest(pagination.next);
    } else if (!pagination.next && !pagination.previous) {
      this.fetchNewChargersList();
    }
  }

  render() {
    const mapChargers = this.props.mappedChargers.map(pk => this.props.chargersById[pk]);

    const selectedCharger = this.props.chargersById[this.props.chargerMap.selectedCharger] || null;

    return (
      <div className='container-fluid' styleName='root'>
        <Helmet
          titleTemplate='Chargerville'
          htmlAttributes={{ class: classNames(this.context.appClass, htmlSizeClass) }}
          meta={[
            { name: 'description', content: 'Chargerville is a platform for electric vehicle drivers to share reviews, tips, and comments about charger locations and nearby restaurants, shops, hotels and attractions.' }, // eslint-disable-line max-len
          ]}
        />

        <div styleName='filters'>

          <Checkbox
            name='filter_superchargers'
            label='Superchargers'
            value={this.props.filters.superchargers}
            onChange={this.handleSuperchargersCheckbox}
          />

          <Checkbox
            name='filter_destination_chargers'
            label='Destination Chargers'
            value={this.props.filters.destinationChargers}
            onChange={this.handleDestinationChargersCheckbox}
          />

        </div>

        <div styleName='map-row'>

          <div styleName='map-inner'>

            <div styleName='map-container-wrapper'>

              <MapContainer animateChargerHover styleName='map-container'>
                <ChargerMap
                  ref={(c) => { this.chargerMapComponent = c; }}
                  chargers={mapChargers}
                  center={this.props.chargerMap.center}
                  zoom={this.props.chargerMap.zoom}
                  onClick={this.handleMapClick}
                  onMarkerClick={this.handleMarkerClick}
                  onClusterClick={this.handleClusterClick}
                  onBoundsChange={this.handleMapBoundsChange}
                  selectedCharger={this.props.chargerMap.selectedCharger}
                  hoveredCharger={this.state.chargerHover}
                  onMapLoaded={this.onMapLoaded}
                  onChargerMouseEnter={chargerId => this.setState({ markerHover: chargerId })}
                  onChargerMouseLeave={() => this.setState({ markerHover: null })}
                  currentLocation={
                    this.props.currentLocation ? this.props.currentLocation.coords : null
                  }
                />
              </MapContainer>

            </div>

            <ChargerList
              chargers={this.props.chargersNearUser.filtered || []}
              chargersById={this.props.chargersById}
              selectedCharger={this.props.chargerMap.selectedCharger}
              hoveredCharger={this.state.markerHover}
              onLoadMore={this.handleLoadMoreChargers}
              loading={this.props.chargersNearUser.loading}
              onChargerMouseEnter={chargerId => this.setState({ chargerHover: chargerId })}
              onChargerMouseLeave={() => this.setState({ chargerHover: null })}
            />

          </div>

        </div>

        <div className={selectedCharger ? 'open' : 'closed'} styleName='popup-row'>
          <div styleName='mobile-charger-popup'>
            <div styleName='content-wrapper'>
              { selectedCharger ?
                <ChargerPreview charger={selectedCharger} />
              : null }
            </div>
          </div>
        </div>

      </div>
    );
  }
}

const mapStateToProps = state => ({
  chargers: state.chargers.filteredChargers,
  mappedChargers: state.chargers.mappedChargers,
  chargersById: state.chargers.byId,
  filters: state.chargers.filters,
  locationFromIp: state.chargers.locationFromIp,
  chargerMap: state.chargers.chargerMap,
  currentLocation: state.chargers.geolocation,
  centerOnCurrentLocation: state.chargers.centerOnCurrentLocation,
  chargersNearUser: state.chargers.nearUser,
});

const chargersNearUserRequest = chargersNearUser.request;
const chargersNearUserNextRequest = chargersNearUserNext.request;
const geolocationSuccess = geolocation.success;
const geolocationFailure = geolocation.failure;

export default connect(
  mapStateToProps, {
    fetchChargers,
    chargersNearUserRequest,
    chargersNearUserNextRequest,
    fetchChargersNearIp,
    filterChargers,
    changeMap,
    chargerMapClick,
    geolocationSuccess,
    geolocationFailure,
    setCenterOnCurrentLocation,
  })(Home);



// WEBPACK FOOTER //
// ./app/scripts/views/App/views/Home/index.jsx